import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { softDelete } from '../services/firestoreService';
import { formatDate, getStatusColor } from '../utils/labelHelpers';
import '../styles/ActionReportsList.css';

const ActionReportsList = ({ candidateId, candidate }) => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [actionReports, setActionReports] = useState([]);
  const [showAddReport, setShowAddReport] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newReport, setNewReport] = useState({
    title: '',
    date: '',
    interviewTeam: '',
    actionItems: {
      certificationStatus: []
    },
    vote: { yes: 0, no: 0, abstain: 0 },
    notes: '',
    nextSteps: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchActionReports();
  }, [candidateId]);

  const fetchActionReports = async () => {
    try {
      const reportsRef = collection(db, 'actionReports');
      const q = query(reportsRef, where('candidateId', '==', candidateId));
      const snapshot = await getDocs(q);
      
      const reports = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      reports.sort((a, b) => new Date(b.date) - new Date(a.date));
      setActionReports(reports);
    } catch (error) {
      console.error('Error fetching action reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddReport = async () => {
    if (!newReport.title || !newReport.date) {
      toast.warning('Please fill in required fields (Title and Date)');
      return;
    }

    try {
      const reportData = {
        ...newReport,
        candidateId,
        candidateName: `${candidate.firstName} ${candidate.lastName}`,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'actionReports'), reportData);
      await fetchActionReports();

      setNewReport({
        title: '',
        date: '',
        interviewTeam: '',
        actionItems: { certificationStatus: [] },
        vote: { yes: 0, no: 0, abstain: 0 },
        notes: '',
        nextSteps: '',
        status: 'pending'
      });
      setShowAddReport(false);
      toast.success('Action report added.');
    } catch (error) {
      console.error('Error adding action report:', error);
      toast.error('Error adding action report. Please try again.');
    }
  };

  const handleEditReport = (report) => {
    setEditingReport({
      ...report,
      date: report.date ? new Date(report.date).toISOString().split('T')[0] : ''
    });
  };

  const handleUpdateReport = async () => {
    if (!editingReport.title || !editingReport.date) {
      toast.warning('Please fill in required fields (Title and Date)');
      return;
    }

    try {
      const reportRef = doc(db, 'actionReports', editingReport.id);
      const updateData = { ...editingReport, updatedAt: new Date() };
      delete updateData.id;

      await updateDoc(reportRef, updateData);
      await fetchActionReports();
      setEditingReport(null);
      toast.success('Action report updated.');
    } catch (error) {
      console.error('Error updating action report:', error);
      toast.error('Error updating action report. Please try again.');
    }
  };

  const handleDeleteReport = async (reportId) => {
    const confirmed = await confirm({
      title: 'Delete Action Report',
      message: 'Are you sure you want to delete this action report?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await softDelete('actionReports', reportId, currentUser);
      await fetchActionReports();
      toast.success('Action report deleted.');
    } catch (error) {
      console.error('Error deleting action report:', error);
      toast.error('Error deleting action report. Please try again.');
    }
  };

  const getActionItemLabel = (item) => {
    const labels = {
      'attend-otm': 'Attend OTM',
      'grant-certification': 'Grant Certification (Three-Fourths Majority Vote)',
      'continue-certification': 'Continue Certification',
      'recommend-first-licensing': 'Recommend First Licensing',
      'full-time-service': 'Full-Time Service',
      'part-time-service': 'Part-Time Service',
      'recommend-continued-licensing': 'Recommend Continued Licensing',
      'rc-full-time-service': 'Full-Time Service',
      'rc-part-time-service': 'Part-Time Service',
      'recommend-associate-membership': 'Recommend to the BOM for Associate Membership',
      'recommend-provisional-deacon': 'Recommend to the BOM for Provisional Membership (Deacon)',
      'recommend-provisional-elder': 'Recommend to the BOM for Provisional Membership (Elder)',
      'discontinue-candidacy': 'Discontinuance of certified candidacy'
    };
    return labels[item] || item;
  };

  const handleCheckboxChange = (item, checked) => {
    const items = newReport.actionItems?.certificationStatus || [];
    if (checked) {
      setNewReport({
        ...newReport,
        actionItems: {
          ...newReport.actionItems,
          certificationStatus: [...items, item]
        }
      });
    } else {
      setNewReport({
        ...newReport,
        actionItems: {
          ...newReport.actionItems,
          certificationStatus: items.filter(i => i !== item)
        }
      });
    }
  };

  const handleEducationalCheckboxChange = (item, checked) => {
    const items = newReport.actionItems?.educationalRequirements || [];
    if (checked) {
      setNewReport({
        ...newReport,
        actionItems: {
          ...newReport.actionItems,
          educationalRequirements: [...items, item]
        }
      });
    } else {
      setNewReport({
        ...newReport,
        actionItems: {
          ...newReport.actionItems,
          educationalRequirements: items.filter(i => i !== item)
        }
      });
    }
  };

  const handleDocumentationCheckboxChange = (item, checked) => {
    const items = newReport.actionItems?.documentation || [];
    if (checked) {
      setNewReport({
        ...newReport,
        actionItems: {
          ...newReport.actionItems,
          documentation: [...items, item]
        }
      });
    } else {
      setNewReport({
        ...newReport,
        actionItems: {
          ...newReport.actionItems,
          documentation: items.filter(i => i !== item)
        }
      });
    }
  };

  const handleMinistryCheckboxChange = (item, checked) => {
    const items = newReport.actionItems?.ministryExperience || [];
    if (checked) {
      setNewReport({
        ...newReport,
        actionItems: {
          ...newReport.actionItems,
          ministryExperience: [...items, item]
        }
      });
    } else {
      setNewReport({
        ...newReport,
        actionItems: {
          ...newReport.actionItems,
          ministryExperience: items.filter(i => i !== item)
        }
      });
    }
  };

  const handleFollowUpCheckboxChange = (item, checked) => {
    const items = newReport.actionItems?.followUp || [];
    if (checked) {
      setNewReport({
        ...newReport,
        actionItems: {
          ...newReport.actionItems,
          followUp: [...items, item]
        }
      });
    } else {
      setNewReport({
        ...newReport,
        actionItems: {
          ...newReport.actionItems,
          followUp: items.filter(i => i !== item)
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="action-reports-list">
        <div className="loading-spinner">Loading action reports...</div>
      </div>
    );
  }

  return (
    <div className="action-reports-list">
      <div className="reports-header">
        <h2>Action Reports & Votes</h2>
        <button 
          onClick={() => setShowAddReport(true)}
          className="add-report-btn"
        >
          Add Action Report
        </button>
      </div>

      {showAddReport && (
        <div className="add-report-modal">
          <div className="modal-content">
            <h3>Add New Action Report</h3>
            <div className="report-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={newReport.title}
                    onChange={(e) => setNewReport({...newReport, title: e.target.value})}
                    placeholder="e.g., DCOM Annual Review 2024"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={newReport.date}
                    onChange={(e) => setNewReport({...newReport, date: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Interview team</label>
                <input
                  type="text"
                  value={newReport.interviewTeam}
                  onChange={(e) => setNewReport({...newReport, interviewTeam: e.target.value})}
                  placeholder="e.g., Rev. Smith, Rev. Johnson, Dr. Williams"
                />
              </div>

          <div className="action-categories">
            <h4>Action Items</h4>
            
            <div className="action-category">
              <h5>Certification Status:</h5>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.certificationStatus?.includes('attend-otm') || false}
                    onChange={(e) => handleCheckboxChange('attend-otm', e.target.checked)}
                  />
                  <span>Attend OTM</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.certificationStatus?.includes('grant-certification') || false}
                    onChange={(e) => handleCheckboxChange('grant-certification', e.target.checked)}
                  />
                  <span>Grant Certification (Three-Fourths Majority Vote)</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.certificationStatus?.includes('continue-certification') || false}
                    onChange={(e) => handleCheckboxChange('continue-certification', e.target.checked)}
                  />
                  <span>Continue Certification</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.certificationStatus?.includes('recommend-first-licensing') || false}
                    onChange={(e) => handleCheckboxChange('recommend-first-licensing', e.target.checked)}
                  />
                  <span>Recommend First Licensing</span>
                  <div className="sub-options">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={newReport.actionItems?.certificationStatus?.includes('full-time-service') || false}
                        onChange={(e) => handleCheckboxChange('full-time-service', e.target.checked)}
                      /> 
                      Full-Time Service
                    </label>
                    <label>
                      <input 
                        type="checkbox" 
                        checked={newReport.actionItems?.certificationStatus?.includes('part-time-service') || false}
                        onChange={(e) => handleCheckboxChange('part-time-service', e.target.checked)}
                      /> 
                      Part-Time Service
                    </label>
                  </div>
                </label>
                

               <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.certificationStatus?.includes('recommend-continued-licensing') || false}
                    onChange={(e) => handleCheckboxChange('recommend-continued-licensing', e.target.checked)}
                  />
                  <span>Recommend Continued Licensing</span>
                  <div className="sub-options">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={newReport.actionItems?.certificationStatus?.includes('rc-full-time-service') || false}
                        onChange={(e) => handleCheckboxChange('rc-full-time-service', e.target.checked)}
                      /> 
                      Full-Time Service
                    </label>
                    <label>
                      <input 
                        type="checkbox" 
                        checked={newReport.actionItems?.certificationStatus?.includes('rc-part-time-service') || false}
                        onChange={(e) => handleCheckboxChange('rc-part-time-service', e.target.checked)}
                      /> 
                      Part-Time Service
                    </label>
                  </div>
                </label>



                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.certificationStatus?.includes('recommend-associate-membership') || false}
                    onChange={(e) => handleCheckboxChange('recommend-associate-membership', e.target.checked)}
                  />
                  <span>Recommend to the BOM for Associate Membership (Three-Fourths Majority Vote)</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.certificationStatus?.includes('recommend-provisional-deacon') || false}
                    onChange={(e) => handleCheckboxChange('recommend-provisional-deacon', e.target.checked)}
                  />
                  <span>Recommend to the BOM for Provisional Membership (Deacon) (Three-Fourths Majority Vote)</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.certificationStatus?.includes('recommend-provisional-elder') || false}
                    onChange={(e) => handleCheckboxChange('recommend-provisional-elder', e.target.checked)}
                  />
                  <span>Recommend to the BOM for Provisional Membership (Elder) (Three-Fourths Majority Vote)</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={newReport.actionItems?.certificationStatus?.includes('discontinue-candidacy') || false}
                    onChange={(e) => handleCheckboxChange('discontinue-candidacy', e.target.checked)}
                  />
                  <span>Discontinuance of certified candidacy</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={newReport.actionItems?.certificationStatus?.includes('supply-pastor-cert-status') || false}
                    onChange={(e) => handleCheckboxChange('supply-pastor-cert-status', e.target.checked)}
                  />
                  <span>Supply Pastor</span>
                  <div className="sub-options">
                    <label>
                      <input
                        type="checkbox"
                        checked={newReport.actionItems?.certificationStatus?.includes('supply-pastor-first-year') || false}
                        onChange={(e) => handleCheckboxChange('supply-pastor-first-year', e.target.checked)}
                      />
                      First Year Supply
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={newReport.actionItems?.certificationStatus?.includes('supply-pastor-out-of-compliance') || false}
                        onChange={(e) => handleCheckboxChange('supply-pastor-out-of-compliance', e.target.checked)}
                      />
                      More Than 1 Year (Out of Compliance)
                    </label>
                  </div>
                </label>
              </div>
            </div>

            <div className="action-category">
              <h5>Educational Requirements:</h5>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.educationalRequirements?.includes('complete-bachelor-degree') || false}
                    onChange={(e) => handleEducationalCheckboxChange('complete-bachelor-degree', e.target.checked)}
                  />
                  <span>Complete Bachelor's Degree</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.educationalRequirements?.includes('complete-mdiv-degree') || false}
                    onChange={(e) => handleEducationalCheckboxChange('complete-mdiv-degree', e.target.checked)}
                  />
                  <span>Complete Master of Divinity Degree</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.educationalRequirements?.includes('complete-course-of-study') || false}
                    onChange={(e) => handleEducationalCheckboxChange('complete-course-of-study', e.target.checked)}
                  />
                  <span>Complete Course of Study</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.educationalRequirements?.includes('submit-transcripts') || false}
                    onChange={(e) => handleEducationalCheckboxChange('submit-transcripts', e.target.checked)}
                  />
                  <span>Submit Official Transcripts</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.educationalRequirements?.includes('complete-cpe') || false}
                    onChange={(e) => handleEducationalCheckboxChange('complete-cpe', e.target.checked)}
                  />
                  <span>Complete Clinical Pastoral Education (CPE)</span>
                </label>
              </div>
            </div>

            <div className="action-category">
              <h5>Documentation & Paperwork:</h5>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.documentation?.includes('submit-psychological-report') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('submit-psychological-report', e.target.checked)}
                  />
                  <span>Submit Psychological Assessment Report</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.documentation?.includes('submit-background-check') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('submit-background-check', e.target.checked)}
                  />
                  <span>Submit Background Check</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.documentation?.includes('submit-credit-check') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('submit-credit-check', e.target.checked)}
                  />
                  <span>Submit Credit Check</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.documentation?.includes('update-candidacy-papers') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('update-candidacy-papers', e.target.checked)}
                  />
                  <span>Update Candidacy Papers</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.documentation?.includes('submit-sermon-manuscript') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('submit-sermon-manuscript', e.target.checked)}
                  />
                  <span>Submit Sermon Manuscript</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.documentation?.includes('submit-theological-statement') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('submit-theological-statement', e.target.checked)}
                  />
                  <span>Submit Theological Statement</span>
                </label>
              </div>
            </div>

            <div className="action-category">
              <h5>Ministry Experience:</h5>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.ministryExperience?.includes('complete-internship') || false}
                    onChange={(e) => handleMinistryCheckboxChange('complete-internship', e.target.checked)}
                  />
                  <span>Complete Ministry Internship</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.ministryExperience?.includes('serve-student-appointment') || false}
                    onChange={(e) => handleMinistryCheckboxChange('serve-student-appointment', e.target.checked)}
                  />
                  <span>Serve Student Appointment</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.ministryExperience?.includes('complete-mentoring-program') || false}
                    onChange={(e) => handleMinistryCheckboxChange('complete-mentoring-program', e.target.checked)}
                  />
                  <span>Complete Mentoring Program</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.ministryExperience?.includes('attend-licensing-school') || false}
                    onChange={(e) => handleMinistryCheckboxChange('attend-licensing-school', e.target.checked)}
                  />
                  <span>Attend Licensing School</span>
                </label>
              </div>
            </div>

            <div className="action-category">
              <h5>Follow-up Actions:</h5>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.followUp?.includes('schedule-next-interview') || false}
                    onChange={(e) => handleFollowUpCheckboxChange('schedule-next-interview', e.target.checked)}
                  />
                  <span>Schedule Next Interview</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.followUp?.includes('meet-with-mentor') || false}
                    onChange={(e) => handleFollowUpCheckboxChange('meet-with-mentor', e.target.checked)}
                  />
                  <span>Meet with Assigned Mentor</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.followUp?.includes('attend-retreat') || false}
                    onChange={(e) => handleFollowUpCheckboxChange('attend-retreat', e.target.checked)}
                  />
                  <span>Attend Candidacy Retreat</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.followUp?.includes('complete-spiritual-direction') || false}
                    onChange={(e) => handleFollowUpCheckboxChange('complete-spiritual-direction', e.target.checked)}
                  />
                  <span>Complete Spiritual Direction Sessions</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={newReport.actionItems?.followUp?.includes('submit-progress-report') || false}
                    onChange={(e) => handleFollowUpCheckboxChange('submit-progress-report', e.target.checked)}
                  />
                  <span>Submit Progress Report</span>
                </label>
              </div>
            </div>
          </div>

              <div className="vote-section">
                <label>Vote Count</label>
                <div className="vote-inputs">
                  <div className="vote-input">
                    <label>Yes</label>
                    <input
                      type="number"
                      min="0"
                      value={newReport.vote.yes}
                      onChange={(e) => setNewReport({
                        ...newReport, 
                        vote: {...newReport.vote, yes: parseInt(e.target.value) || 0}
                      })}
                    />
                  </div>
                  <div className="vote-input">
                    <label>No</label>
                    <input
                      type="number"
                      min="0"
                      value={newReport.vote.no}
                      onChange={(e) => setNewReport({
                        ...newReport, 
                        vote: {...newReport.vote, no: parseInt(e.target.value) || 0}
                      })}
                    />
                  </div>
                  <div className="vote-input">
                    <label>Abstain</label>
                    <input
                      type="number"
                      min="0"
                      value={newReport.vote.abstain}
                      onChange={(e) => setNewReport({
                        ...newReport, 
                        vote: {...newReport.vote, abstain: parseInt(e.target.value) || 0}
                      })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newReport.notes}
                  onChange={(e) => setNewReport({...newReport, notes: e.target.value})}
                  rows="4"
                  placeholder="Meeting notes, discussion points, concerns..."
                />
              </div>
              
              <div className="form-group">
                <label>Next Steps</label>
                <textarea
                  value={newReport.nextSteps}
                  onChange={(e) => setNewReport({...newReport, nextSteps: e.target.value})}
                  rows="3"
                  placeholder="Required actions, follow-up items..."
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  value={newReport.status}
                  onChange={(e) => setNewReport({...newReport, status: e.target.value})}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="denied">Denied</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowAddReport(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleAddReport} className="save-btn">
                Save Action Report
              </button>
            </div>
          </div>
        </div>
      )}

      {editingReport && (
        <EditReportModal
          report={editingReport}
          setReport={setEditingReport}
          onSave={handleUpdateReport}
          onCancel={() => setEditingReport(null)}
        />
      )}

      <div className="reports-list">
        {actionReports.length === 0 ? (
          <div className="no-reports">
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem', opacity: 0.4 }}>📋</div>
            <h4 style={{ margin: '0 0 0.5rem', color: '#2c3e50' }}>No action reports yet</h4>
            <p style={{ margin: '0 0 1.25rem', color: '#6c757d', fontSize: '0.875rem' }}>
              Record committee decisions, vote counts, and interview notes from dCOM meetings here.
            </p>
            <button onClick={() => setShowAddReport(true)} className="add-report-btn">
              Add First Report
            </button>
          </div>
        ) : (
          actionReports.map(report => (
            <ActionReportCard 
              key={report.id} 
              report={report} 
              onEdit={() => handleEditReport(report)}
              onDelete={() => handleDeleteReport(report.id)}
              getActionItemLabel={getActionItemLabel}
              formatDate={formatDate}
              getStatusColor={getStatusColor}
            />
          ))
        )}
      </div>
    </div>
  );
};

const ActionReportCard = ({ report, onEdit, onDelete, getActionItemLabel, formatDate, getStatusColor }) => {
  const getTotalVotes = () => {
    return (report.vote?.yes || 0) + (report.vote?.no || 0) + (report.vote?.abstain || 0);
  };

  return (
    <div className="action-report-card">
      <div className="report-header">
        <div className="report-title">
          <h4>{report.title}</h4>
          <div className="report-meta">
            <span className="report-type">Status:</span>
            <span className={`report-status ${getStatusColor(report.status)}`}>
              {report.status?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          </div>
        </div>
        <div className="report-actions">
          <button onClick={onEdit} className="edit-btn">Edit</button>
          <button onClick={onDelete} className="delete-btn">Delete</button>
        </div>
      </div>

      <div className="report-details">
        <div className="report-info">
          <div className="info-row">
            <div className="info-item">
              <label>Date:</label>
              <span>{formatDate(report.date)}</span>
            </div>
            <div className="info-item">
              <label>Interview team:</label>
              <span>{report.interviewTeam || 'Not specified'}</span>
            </div>
          </div>

          <div className="vote-summary">
            <label>Vote Results:</label>
            <div className="vote-display">
              <span className="vote-yes">Yes: {report.vote?.yes || 0}</span>
              <span className="vote-no">No: {report.vote?.no || 0}</span>
              <span className="vote-abstain">Abstain: {report.vote?.abstain || 0}</span>
              <span className="vote-total">Total: {getTotalVotes()}</span>
            </div>
          </div>

          {report.notes && (
            <div className="info-item full-width">
              <label>Notes:</label>
              <div className="notes-content">{report.notes}</div>
            </div>
          )}

          {report.nextSteps && (
            <div className="info-item full-width">
              <label>Next Steps:</label>
              <div className="next-steps-content">{report.nextSteps}</div>
            </div>
          )}

          {report.actionItems && report.actionItems.certificationStatus && report.actionItems.certificationStatus.length > 0 && (
            <div className="info-item full-width">
              <label style={{margin: '.25rem 0'}}>Selected Action Items:</label>
              <div className="action-items-display" style={{padding: '0 .25rem'}}>
                <div className="action-category-display">
                  <strong>Certification Status:</strong>
                  <ul style={{padding: '10px 0 0 30px'}}>
                    {report.actionItems.certificationStatus.map((item, index) => (
                      <li key={index}>{getActionItemLabel(item)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EditReportModal = ({ report, setReport, onSave, onCancel }) => {
  const handleCheckboxChange = (item, checked) => {
    const items = report.actionItems?.certificationStatus || [];
    if (checked) {
      setReport({
        ...report,
        actionItems: {
          ...report.actionItems,
          certificationStatus: [...items, item]
        }
      });
    } else {
      setReport({
        ...report,
        actionItems: {
          ...report.actionItems,
          certificationStatus: items.filter(i => i !== item)
        }
      });
    }
  };

  const handleEducationalCheckboxChange = (item, checked) => {
    const items = report.actionItems?.educationalRequirements || [];
    if (checked) {
      setReport({
        ...report,
        actionItems: {
          ...report.actionItems,
          educationalRequirements: [...items, item]
        }
      });
    } else {
      setReport({
        ...report,
        actionItems: {
          ...report.actionItems,
          educationalRequirements: items.filter(i => i !== item)
        }
      });
    }
  };

  const handleDocumentationCheckboxChange = (item, checked) => {
    const items = report.actionItems?.documentation || [];
    if (checked) {
      setReport({
        ...report,
        actionItems: {
          ...report.actionItems,
          documentation: [...items, item]
        }
      });
    } else {
      setReport({
        ...report,
        actionItems: {
          ...report.actionItems,
          documentation: items.filter(i => i !== item)
        }
      });
    }
  };

  const handleMinistryCheckboxChange = (item, checked) => {
    const items = report.actionItems?.ministryExperience || [];
    if (checked) {
      setReport({
        ...report,
        actionItems: {
          ...report.actionItems,
          ministryExperience: [...items, item]
        }
      });
    } else {
      setReport({
        ...report,
        actionItems: {
          ...report.actionItems,
          ministryExperience: items.filter(i => i !== item)
        }
      });
    }
  };

  const handleFollowUpCheckboxChange = (item, checked) => {
    const items = report.actionItems?.followUp || [];
    if (checked) {
      setReport({
        ...report,
        actionItems: {
          ...report.actionItems,
          followUp: [...items, item]
        }
      });
    } else {
      setReport({
        ...report,
        actionItems: {
          ...report.actionItems,
          followUp: items.filter(i => i !== item)
        }
      });
    }
  };

  return (
    <div className="add-report-modal">
      <div className="modal-content">
        <h3>Edit Action Report</h3>
        <div className="report-form">
          <div className="form-row">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={report.title || ''}
                onChange={(e) => setReport({...report, title: e.target.value})}
                placeholder="e.g., DCOM Annual Review 2024"
                required
              />
            </div>
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={report.date || ''}
                onChange={(e) => setReport({...report, date: e.target.value})}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Interview team</label>
            <input
              type="text"
              value={report.interviewTeam || ''}
              onChange={(e) => setReport({...report, interviewTeam: e.target.value})}
              placeholder="e.g., Rev. Smith, Rev. Johnson, Dr. Williams"
            />
          </div>

          <div className="action-categories">
            <h4>Action Items</h4>
            
            <div className="action-category">
              <h5>Certification Status:</h5>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.certificationStatus?.includes('attend-otm') || false}
                    onChange={(e) => handleCheckboxChange('attend-otm', e.target.checked)}
                  />
                  <span>Attend OTM</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.certificationStatus?.includes('grant-certification') || false}
                    onChange={(e) => handleCheckboxChange('grant-certification', e.target.checked)}
                  />
                  <span>Grant Certification (Three-Fourths Majority Vote)</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.certificationStatus?.includes('continue-certification') || false}
                    onChange={(e) => handleCheckboxChange('continue-certification', e.target.checked)}
                  />
                  <span>Continue Certification</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.certificationStatus?.includes('recommend-first-licensing') || false}
                    onChange={(e) => handleCheckboxChange('recommend-first-licensing', e.target.checked)}
                  />
                  <span>Recommend First Licensing</span>
                  <div className="sub-options">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={report.actionItems?.certificationStatus?.includes('full-time-service') || false}
                        onChange={(e) => handleCheckboxChange('full-time-service', e.target.checked)}
                      /> 
                      Full-Time Service
                    </label>
                    <label>
                      <input 
                        type="checkbox" 
                        checked={report.actionItems?.certificationStatus?.includes('part-time-service') || false}
                        onChange={(e) => handleCheckboxChange('part-time-service', e.target.checked)}
                      /> 
                      Part-Time Service
                    </label>
                  </div>
                </label>


                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.certificationStatus?.includes('recommend-continued-licensing') || false}
                    onChange={(e) => handleCheckboxChange('recommend-continued-licensing', e.target.checked)}
                  />
                  <span>Recommend Continued Licensing</span>
                  <div className="sub-options">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={report.actionItems?.certificationStatus?.includes('rc-full-time-service') || false}
                        onChange={(e) => handleCheckboxChange('rc-full-time-service', e.target.checked)}
                      /> 
                      Full-Time Service
                    </label>
                    <label>
                      <input 
                        type="checkbox" 
                        checked={report.actionItems?.certificationStatus?.includes('rc-part-time-service') || false}
                        onChange={(e) => handleCheckboxChange('rc-part-time-service', e.target.checked)}
                      /> 
                      Part-Time Service
                    </label>
                  </div>
                </label>




                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.certificationStatus?.includes('recommend-associate-membership') || false}
                    onChange={(e) => handleCheckboxChange('recommend-associate-membership', e.target.checked)}
                  />
                  <span>Recommend to the BOM for Associate Membership (Three-Fourths Majority Vote)</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.certificationStatus?.includes('recommend-provisional-deacon') || false}
                    onChange={(e) => handleCheckboxChange('recommend-provisional-deacon', e.target.checked)}
                  />
                  <span>Recommend to the BOM for Provisional Membership (Deacon) (Three-Fourths Majority Vote)</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.certificationStatus?.includes('recommend-provisional-elder') || false}
                    onChange={(e) => handleCheckboxChange('recommend-provisional-elder', e.target.checked)}
                  />
                  <span>Recommend to the BOM for Provisional Membership (Elder) (Three-Fourths Majority Vote)</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={report.actionItems?.certificationStatus?.includes('discontinue-candidacy') || false}
                    onChange={(e) => handleCheckboxChange('discontinue-candidacy', e.target.checked)}
                  />
                  <span>Discontinuance of certified candidacy</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={report.actionItems?.certificationStatus?.includes('supply-pastor-cert-status') || false}
                    onChange={(e) => handleCheckboxChange('supply-pastor-cert-status', e.target.checked)}
                  />
                  <span>Supply Pastor</span>
                  <div className="sub-options">
                    <label>
                      <input
                        type="checkbox"
                        checked={report.actionItems?.certificationStatus?.includes('supply-pastor-first-year') || false}
                        onChange={(e) => handleCheckboxChange('supply-pastor-first-year', e.target.checked)}
                      />
                      First Year Supply
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={report.actionItems?.certificationStatus?.includes('supply-pastor-out-of-compliance') || false}
                        onChange={(e) => handleCheckboxChange('supply-pastor-out-of-compliance', e.target.checked)}
                      />
                      More Than 1 Year (Out of Compliance)
                    </label>
                  </div>
                </label>
              </div>
            </div>

            <div className="action-category">
              <h5>Educational Requirements:</h5>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={report.actionItems?.educationalRequirements?.includes('complete-bachelor-degree') || false}
                    onChange={(e) => handleEducationalCheckboxChange('complete-bachelor-degree', e.target.checked)}
                  />
                  <span>Complete Bachelor's Degree</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.educationalRequirements?.includes('complete-mdiv-degree') || false}
                    onChange={(e) => handleEducationalCheckboxChange('complete-mdiv-degree', e.target.checked)}
                  />
                  <span>Complete Master of Divinity Degree</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.educationalRequirements?.includes('complete-course-of-study') || false}
                    onChange={(e) => handleEducationalCheckboxChange('complete-course-of-study', e.target.checked)}
                  />
                  <span>Complete Course of Study</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.educationalRequirements?.includes('submit-transcripts') || false}
                    onChange={(e) => handleEducationalCheckboxChange('submit-transcripts', e.target.checked)}
                  />
                  <span>Submit Official Transcripts</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.educationalRequirements?.includes('complete-cpe') || false}
                    onChange={(e) => handleEducationalCheckboxChange('complete-cpe', e.target.checked)}
                  />
                  <span>Complete Clinical Pastoral Education (CPE)</span>
                </label>
              </div>
            </div>

            <div className="action-category">
              <h5>Documentation & Paperwork:</h5>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.documentation?.includes('submit-psychological-report') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('submit-psychological-report', e.target.checked)}
                  />
                  <span>Submit Psychological Assessment Report</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.documentation?.includes('submit-background-check') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('submit-background-check', e.target.checked)}
                  />
                  <span>Submit Background Check</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.documentation?.includes('submit-credit-check') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('submit-credit-check', e.target.checked)}
                  />
                  <span>Submit Credit Check</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.documentation?.includes('update-candidacy-papers') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('update-candidacy-papers', e.target.checked)}
                  />
                  <span>Update Candidacy Papers</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.documentation?.includes('submit-sermon-manuscript') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('submit-sermon-manuscript', e.target.checked)}
                  />
                  <span>Submit Sermon Manuscript</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.documentation?.includes('submit-theological-statement') || false}
                    onChange={(e) => handleDocumentationCheckboxChange('submit-theological-statement', e.target.checked)}
                  />
                  <span>Submit Theological Statement</span>
                </label>
              </div>
            </div>

            <div className="action-category">
              <h5>Ministry Experience:</h5>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.ministryExperience?.includes('complete-internship') || false}
                    onChange={(e) => handleMinistryCheckboxChange('complete-internship', e.target.checked)}
                  />
                  <span>Complete Ministry Internship</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.ministryExperience?.includes('serve-student-appointment') || false}
                    onChange={(e) => handleMinistryCheckboxChange('serve-student-appointment', e.target.checked)}
                  />
                  <span>Serve Student Appointment</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.ministryExperience?.includes('complete-mentoring-program') || false}
                    onChange={(e) => handleMinistryCheckboxChange('complete-mentoring-program', e.target.checked)}
                  />
                  <span>Complete Mentoring Program</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.ministryExperience?.includes('attend-licensing-school') || false}
                    onChange={(e) => handleMinistryCheckboxChange('attend-licensing-school', e.target.checked)}
                  />
                  <span>Attend Licensing School</span>
                </label>
              </div>
            </div>

            <div className="action-category">
              <h5>Follow-up Actions:</h5>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.followUp?.includes('schedule-next-interview') || false}
                    onChange={(e) => handleFollowUpCheckboxChange('schedule-next-interview', e.target.checked)}
                  />
                  <span>Schedule Next Interview</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.followUp?.includes('meet-with-mentor') || false}
                    onChange={(e) => handleFollowUpCheckboxChange('meet-with-mentor', e.target.checked)}
                  />
                  <span>Meet with Assigned Mentor</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.followUp?.includes('attend-retreat') || false}
                    onChange={(e) => handleFollowUpCheckboxChange('attend-retreat', e.target.checked)}
                  />
                  <span>Attend Candidacy Retreat</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.followUp?.includes('complete-spiritual-direction') || false}
                    onChange={(e) => handleFollowUpCheckboxChange('complete-spiritual-direction', e.target.checked)}
                  />
                  <span>Complete Spiritual Direction Sessions</span>
                </label>
                <label className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={report.actionItems?.followUp?.includes('submit-progress-report') || false}
                    onChange={(e) => handleFollowUpCheckboxChange('submit-progress-report', e.target.checked)}
                  />
                  <span>Submit Progress Report</span>
                </label>
              </div>
            </div>
          </div>

          <div className="vote-section">
            <label>Vote Count</label>
            <div className="vote-inputs">
              <div className="vote-input">
                <label>Yes</label>
                <input
                  type="number"
                  min="0"
                  value={report.vote?.yes || 0}
                  onChange={(e) => setReport({
                    ...report, 
                    vote: {...(report.vote || {}), yes: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
              <div className="vote-input">
                <label>No</label>
                <input
                  type="number"
                  min="0"
                  value={report.vote?.no || 0}
                  onChange={(e) => setReport({
                    ...report, 
                    vote: {...(report.vote || {}), no: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
              <div className="vote-input">
                <label>Abstain</label>
                <input
                  type="number"
                  min="0"
                  value={report.vote?.abstain || 0}
                  onChange={(e) => setReport({
                    ...report, 
                    vote: {...(report.vote || {}), abstain: parseInt(e.target.value) || 0}
                  })}
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={report.notes || ''}
              onChange={(e) => setReport({...report, notes: e.target.value})}
              rows="4"
              placeholder="Meeting notes, discussion points, concerns..."
            />
          </div>
          
          <div className="form-group">
            <label>Next Steps</label>
            <textarea
              value={report.nextSteps || ''}
              onChange={(e) => setReport({...report, nextSteps: e.target.value})}
              rows="3"
              placeholder="Required actions, follow-up items..."
            />
          </div>
          
          <div className="form-group">
            <label>Status</label>
            <select
              value={report.status || 'pending'}
              onChange={(e) => setReport({...report, status: e.target.value})}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <div className="modal-actions">
          <button onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button onClick={onSave} className="save-btn">
            Update Action Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionReportsList;
